import Button from "@components/Button";
import {Translate} from "next-translate";
import {useTranslation} from "ni18n";
import React from "react";

import {testColors} from "../../../constants/Reason/getCareDiscoveryColorScheme";
import {CareInsuranceType} from "../../../utils/cms/types";

interface WidthVariant {
  isFullWidth: boolean;
}

const Header: React.FC<{text: string; reduceBottomPadding: boolean} & WidthVariant> = ({
  text,
  isFullWidth,
  reduceBottomPadding,
}) =>
  React.createElement(
    isFullWidth ? "h1" : "strong",
    {
      className: `font-csb white fsn ls-25 lh12 ${
        isFullWidth
          ? "fs48 fs28-lg fs24-sm tac tal-lg ph12 ph0-lg pb18 pb10-lg pb8-sm"
          : reduceBottomPadding
          ? "fs40 fs30-md fs24-sm pb0"
          : "fs40 fs30-md fs24-sm pb10 pb8-sm"
      }`,
    },
    text,
  );

const CallPrompt: React.FC<WidthVariant> = ({isFullWidth}) => {
  const i18n = useTranslation();

  return (
    <div
      className={`font-cm fs24 lh13 ls-3 fs18-sm flex justify-center items-center gap-4 pb10 ${
        isFullWidth ? "tac tal-lg" : "mt10 "
      }`}
    >
      <span style={{color: testColors.lightBlue}}>
        {i18n.t("Contact your insurance company to check your coverage.")}
      </span>
      <Button href="#faq" size="sm" variant="white" className="">
        {i18n.t("Learn More")}
      </Button>
    </div>
  );
};

export const getCareInsuranceText = (
  t: Translate,
  insuranceType?: CareInsuranceType,
  contentTitle = "this",
): string => {
  switch (insuranceType) {
    case CareInsuranceType.OccupationalHealth:
      return t(
        "If your employer has sent you to Carbon Health for a workplace health visit, the cost is typically covered by your employer or reimbursed afterwards.",
      );
    case CareInsuranceType.Standard:
      return t(
        "We accept most major insurance plans. No insurance? No problem, our pricing is clear and affordable.",
      );
    case CareInsuranceType.Medicaid:
      return t(
        "The only insurance we accept for {{conditionSymtomOrTopic}} is Medicaid. No Medicaid? No problem, our pricing is clear and affordable.",
        {
          conditionSymtomOrTopic: contentTitle,
        },
      );
    case CareInsuranceType.NoInsurance:
      return t("Insurance is not accepted for {{conditionSymtomOrTopic}}.", {
        conditionSymtomOrTopic: contentTitle,
      });
    default:
      return getCareInsuranceText(t, CareInsuranceType.Standard);
  }
};
export interface InsurancesProps extends WidthVariant {
  headerText: string;
  insuranceType?: CareInsuranceType;
}

export const Insurances: React.FC<InsurancesProps> = ({headerText, insuranceType, isFullWidth}) => {
  const i18n = useTranslation();

  /**
   * This component is used as hero at /insurance-pricing, where no insuranceType is present.
   * Allow logos on that page ->
   */

  return React.createElement(
    isFullWidth ? "div" : "section",
    {
      id: "insurance-and-pricing",
      className: `df fdc ${isFullWidth ? "maxw70 ma ph8-xl ph16-lg ph5-sm" : ""}`,
      "aria-label": isFullWidth ? null : i18n.t("Insurance and Pricing"),
    },
    <>
      {!isFullWidth && (
        <h2 className="font-mb ttu white fs12 pb4">{i18n.t("Insurance and pricing")}</h2>
      )}
      <Header isFullWidth={isFullWidth} text={headerText} reduceBottomPadding />
      <CallPrompt isFullWidth={isFullWidth} />
    </>,
  );
};

export default Insurances;
